import { useEffect, useState } from 'react';

/**
 * Quelle surface regarde-t-on ?
 *
 * « **Le telephone porte l'usage. L'ordinateur porte la comprehension.** » Et surtout :
 * **aucune fonction n'est reservee a l'un des deux** (SPEC 1). Ce qui change n'est pas ce
 * qu'on peut faire, c'est **combien on voit d'un seul regard** — l'ordinateur montre la
 * forme, le telephone la raconte.
 *
 * Ce fichier ne sert donc JAMAIS a retirer une capacite. Il sert a choisir un rendu.
 * Si un jour une condition ici garde une action pour le grand ecran, c'est le risque
 * « l'ordinateur devient obligatoire » de la 10 qui vient de se realiser.
 */
const AU_POUCE = '(max-width: 720px)';

export function useAuPouce(): boolean {
  const [auPouce, setAuPouce] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(AU_POUCE).matches
  );

  useEffect(() => {
    const surveillance = window.matchMedia(AU_POUCE);
    const changer = (e: MediaQueryListEvent) => setAuPouce(e.matches);
    /*
     * Relue aussi au redimensionnement, comme `useCouche` (chantier telephone-fiche, 02) : quand le
     * téléphone tourne, les deux changent dans le même rendu. Sans ça, entre « plus couché » et
     * « au pouce », l'écran passait un instant au cadre de l'ordinateur, et la page ouverte (une
     * fiche comprise) se perdait.
     */
    const relire = () => setAuPouce(surveillance.matches);
    surveillance.addEventListener('change', changer);
    window.addEventListener('resize', relire);
    return () => {
      surveillance.removeEventListener('change', changer);
      window.removeEventListener('resize', relire);
    };
  }, []);

  return auPouce;
}

/**
 * Le téléphone couché (chantier telephone-couche, 01) : un écran en paysage, au doigt, de 500 px
 * de haut au plus. Une fenêtre d'ordinateur, même large et basse, n'est jamais un téléphone
 * couché : elle se mène à la souris. La même règle, en requête, pose les jetons du carnet
 * (`styles/ambiances/carnet-au-pouce.css`, `COUCHE`).
 */
export const HAUTEUR_DU_TELEPHONE_COUCHE = 500;

export function estCouche(ecran: { largeur: number; hauteur: number; auDoigt: boolean }): boolean {
  return ecran.auDoigt && ecran.largeur > ecran.hauteur && ecran.hauteur <= HAUTEUR_DU_TELEPHONE_COUCHE;
}

const AU_DOIGT = '(pointer: coarse)';

function ecranCouche(): boolean {
  if (typeof window === 'undefined') return false;
  return estCouche({
    largeur: window.innerWidth,
    hauteur: window.innerHeight,
    auDoigt: window.matchMedia(AU_DOIGT).matches,
  });
}

/** Le téléphone est-il couché ? Suit le téléphone qui tourne. */
export function useCouche(): boolean {
  const [couche, setCouche] = useState(ecranCouche);

  useEffect(() => {
    const relire = () => setCouche(ecranCouche());
    window.addEventListener('resize', relire);
    window.addEventListener('orientationchange', relire);
    return () => {
      window.removeEventListener('resize', relire);
      window.removeEventListener('orientationchange', relire);
    };
  }, []);

  return couche;
}
